import React, { useEffect, useState } from 'react'

import { Card } from 'components'
import { SuccessModal } from 'components/SuccessModal/SuccessModal'
import { useLoading, useModal, useAuth, usePersistForm } from 'hooks'
import moment from 'moment'
import {
  SearchDocuments,
  SearchDocumentsServices
} from 'services/Supplier/ThirdPartyAdministration'
import { COLORS, SPACING } from 'utils'
import { filterObject } from 'utils/functions'
import { TipoPendencia } from 'utils/types'

import { SearchFields, TableSearch } from './components'
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

interface SearchProps {
  documentTypes: TipoPendencia[]
  // handleDownloadDocument: (_externalID: number) => void
}

export interface SearchParamsProps {
  CdContrato: string
  TipoDoc: number
  Status: string
  CompetenciaInicio: string
  CompetenciaFim: string
  DataAnexadaInicio: string
  DataAnexadaFim: string
  enumFilter: number
  NmFornecedor: string
}

const SCHEMA = yup.object({
  CdContrato: yup.string().nullable().notRequired(),
  TipoDoc: yup.string().nullable().notRequired(),
  Status: yup.string().nullable().notRequired(),
  CompetenciaInicio: yup
    .date()
    .nullable()
    .notRequired()
    .typeError('Digite uma data válida.'),
  CompetenciaFim: yup
    .date()
    .nullable()
    .notRequired()
    .typeError('Digite uma data válida.')
    .min(
      yup.ref('CompetenciaInicio'),
      'Competência final deve ser maior que competencia inicial.'
    ),
  DataAnexadaInicio: yup
    .date()
    .nullable()
    .notRequired()
    .typeError('Digite uma data válida.'),
  DataAnexadaFim: yup
    .date()
    .nullable()
    .notRequired()
    .typeError('Digite uma data válida.')
    .min(
      yup.ref('DataAnexadaInicio'),
      'Data anexada fim deve ser maior que Data anexada inicio.'
    ),
  enumFilter: yup.number().required(),
  NmFornecedor: yup.string().nullable().notRequired()
})

export const Search: React.FC<SearchProps> = ({
  // handleDownloadDocument,
  documentTypes
}) => {
  const { loading, msgErro, setLoading, setMsgErro } = useLoading()
  const { showModal } = useModal()
  const {
    user: { IdUsuario }
  } = useAuth()

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<SearchParamsProps>({
    resolver: yupResolver(SCHEMA),
    defaultValues: {
      CdContrato: null,
      CompetenciaFim: null,
      CompetenciaInicio: null,
      DataAnexadaFim: null,
      DataAnexadaInicio: null,
      enumFilter: 2,
      Status: '',
      TipoDoc: null
    }
  })

  usePersistForm('ngt_third_party_administration_search', {
    setValue,
    watch
  })

  const handleReset = () => {
    reset()
    setPage(1)
    setRowsPerPage(15)
    if (page === 1) {
      handleSearch()
    }
  }

  const [page, setPage] = useState<number>(0)
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(15)
  const [rowSize, setRowSize] = React.useState<number>(0)
  const [listDocuments, setListDocuments] = useState<SearchDocuments[]>([])
  let fromPesquisar = false
  let isSearching = false

  const validateDates = (): boolean => {
    const isValid = true
    const notValid = false

    const searchForm = watch()

    const dates = filterObject(
      searchForm,
      (val, key) =>
        key === 'CompetenciaInicio' ||
        key === 'CompetenciaFim' ||
        key === 'DataAnexadaInicio' ||
        key === 'DataAnexadaFim'
    )
    const validDatesStates = Object.values(dates).map((date) =>
      moment(date).isValid()
    )
    const validDatesComp = [validDatesStates[0], validDatesStates[1]]
    const validDatesAnex = [validDatesStates[2], validDatesStates[3]]

    if (JSON.stringify(validDatesComp) === '[false,true]') {
      setLoading('idle')
      showModal({
        hideButtonCancel: true,
        message:
          'Digite uma data válida para o campo de Período Competência Fim',
        txtButtonConfirm: 'OK',
        type: 'error'
      })
      return notValid
    }

    if (JSON.stringify(validDatesAnex) === '[false,true]') {
      setLoading('idle')
      showModal({
        hideButtonCancel: true,
        message:
          'Digite uma data válida para o campo de Período Data Anexada Fim',
        txtButtonConfirm: 'OK',
        type: 'error'
      })
      return notValid
    }

    if (JSON.stringify(validDatesComp) === '[true,false]') {
      setLoading('idle')
      showModal({
        hideButtonCancel: true,
        message:
          'Digite uma data válida para o campo de Período Competência Início',
        txtButtonConfirm: 'OK',
        type: 'error'
      })
      return notValid
    }

    if (JSON.stringify(validDatesAnex) === '[true,false]') {
      setLoading('idle')
      showModal({
        hideButtonCancel: true,
        message:
          'Digite uma data válida para o campo de Período Data Anexada Início',
        txtButtonConfirm: 'OK',
        type: 'error'
      })
      return notValid
    }

    return isValid
  }

  const handleSearch = (
    form: SearchParamsProps = {} as SearchParamsProps,
    fromPesquisar?: boolean
  ) => {
    if(isSearching)return;
    isSearching=true;
    setLoading('loading')

    if (!validateDates()) return
    if (fromPesquisar && page != 1) {
      setPage(1)
      return
    }

    const {
      CdContrato,
      CompetenciaFim,
      CompetenciaInicio,
      DataAnexadaFim,
      DataAnexadaInicio,
      Status,
      TipoDoc,
      NmFornecedor,
      enumFilter
    } = form

    SearchDocumentsServices()
      .get({
        ...(CdContrato && { CdContrato }),
        ...(NmFornecedor && { NmFornecedor }),
        ...(CompetenciaFim && {
          CompetenciaFim: moment(CompetenciaFim).toISOString()
        }),
        ...(CompetenciaInicio && {
          CompetenciaInicio: moment(CompetenciaInicio).toISOString()
        }),
        ...(DataAnexadaFim && {
          DataAnexadaFim: moment(DataAnexadaFim)
            .set({ hour: 0, minute: 0, second: 0 })
            .toISOString()
        }),
        ...(DataAnexadaInicio && {
          DataAnexadaInicio: moment(DataAnexadaInicio)
            .set({ hour: 0, minute: 0, second: 0 })
            .toISOString()
        }),
        ...(Status && { Status: Status }),
        ...(TipoDoc && { TipoPendenciaId: TipoDoc }),
        UsuarioId: IdUsuario,
        enumFilter: enumFilter,
        pageNumber: page,
        pageSize: rowsPerPage
      })
      .then((response) => {
        if (response.status === 200) {
          setListDocuments(response.data.items)
        }

        setRowSize(response.data.pageCount)
        setLoading('idle')
      })
      .catch(() => {
        // setLoading('error')
        // setMsgErro('Não foi possível obter os dados')
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: 'Não foi possível obter os dados',
          txtButtonConfirm: 'Fechar',
          type: 'error'
        })
      }).finally(()=>{
        isSearching=false;
      })
  }

  // useEffect(() => {
  //   handleSearch()
  // }, [page, rowsPerPage])

  useEffect(() => {
    if (!fromPesquisar && page!=0) {
      handleSearch(watch())
    } else {
      fromPesquisar = false
    }
  }, [page, rowsPerPage])

  return (
    <>
      <Card
        marginLeft={SPACING.spacing20}
        marginRight={SPACING.spacing20}
        marginBottom={SPACING.spacing100}
        padding={SPACING.spacing20}
        boxShadow={`0px -1px 32px rgba(50, 50, 71, 0.12), 0px 24px 32px rgba(50, 50, 71, 0.12)`}
        backgroundColor={COLORS.white}
        borderRadius={`0px 10px 0px 0px`}
      >
        <SearchFields
          control={control}
          errors={errors}
          handleSearch={() => handleSearch(watch(), true)}
          handleReset={handleReset}
          handleSubmit={handleSubmit}
          documentTypes={documentTypes}
        />
        {listDocuments.length > 0 && (
          <TableSearch
            tableData={listDocuments}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            rowsSize={rowSize}
            // handleDownloadDocument={handleDownloadDocument}
          />
        )}
      </Card>
      <SuccessModal
        type="error"
        open={loading === 'error'}
        message={msgErro}
        hideButtonCancel
        txtButtonConfirm="OK"
        handleConfirm={() => setLoading('idle')}
      />
    </>
  )
}
